/**
 * Services file for CMS which invokes Kentico API endpoints.
 *
 * NOTE: CRM should have a separate file with the same structure and keys, just different values.
 * This file will be replaced by Gulp, with the CRM equivalent, when generating the build assets for CRM.
 */

import { getCurrentDate } from '@general/js/utils/dateTimeUtils';

const BASE_API_URL = '/api';
const BASE_CRM_API_URL = '/api/data/v9.1';
const BASE_BENEFIT_QUOTE_URL = `${BASE_API_URL}/benefit-quote`;
const BASE_CLAIMS_URL = `${BASE_API_URL}/claims`;
const BASE_LIMITS_USAGE_URL = `${BASE_API_URL}/limits-and-usage`;
const BASE_KLEBER_URL = `${BASE_API_URL}/address`;
const BASE_MEMBERSHIP_CARD_URL = `${BASE_API_URL}/membership`;
const BASE_REBATE_CHANGE_URL = `${BASE_API_URL}/rebate-tier`;
const BASE_TAX_STATEMENTS_URL = `${BASE_API_URL}/tax-statements`;
const BASE_MY_DOCUMENTS_URL = `${BASE_API_URL}/document`;
const BASE_PAYMENTS_URL = `${BASE_API_URL}/payments`;
const BASE_MAKE_A_PAYMENT_URL = `${BASE_PAYMENTS_URL}/make-a-payment`;
const BASE_MEMBERSHIP_DETAILS_URL = `${BASE_API_URL}/membership-details`;
const BASE_MEMBER_DETAILS_URL = `${BASE_API_URL}/member-details`;
const BASE_PAYMENT_DETAILS_REF_DATA_URL = `${BASE_API_URL}/payment-details/reference-data`;
const BASE_CHANGE_COVER_URL = `${BASE_API_URL}/change-cover`;
const BASE_CHANGE_EXCESS_URL = `${BASE_API_URL}/change-excess`;
const BASE_COMMON_URL = `${BASE_API_URL}/common`;
const BASE_REBATES_URL = `${BASE_API_URL}/rebates`;

export const Services = {
  COMMON: {
    // reusing the additional reference data used by JOL flow
    ADDITIONAL_INFORMATION_REFERENCE_DATA: `${BASE_API_URL}/join-now`,
    GET_NAVIGATION_AND_MEMBER_DETAILS: `${BASE_API_URL}/navigation`,
    REFRESH_COOKIE: `${BASE_API_URL}/portal/keep-alive`,
    SEARCH: {
      GET_SEARCH_PREDICTION: `${BASE_API_URL}/search/suggestions`,
      GET_SEARCH_RESULTS: `${BASE_API_URL}/search/portal`,
    },
    BASE_API_URL,
    POST_COMMON_WORK_ITEM: (memberNumber) => {
      return `${BASE_COMMON_URL}/work-item?memberNumber=${memberNumber}`;
    },
    GET_RETRIEVE_MEMBERSHIP_DETAILS: (memberNumber, currentDate) => {
      return `${BASE_API_URL}/membership/${memberNumber}?currentDate=${currentDate}`;
    },
    GET_MEMBER_POLICIES: (memberNumber, accessingPersonCode, currentDate) => {
      return `${BASE_API_URL}/membership/${memberNumber}/member/${accessingPersonCode}/fetch-member-policies?currentDate=${currentDate}`;
    },
  },
  MY_CLAIMS: {
    BENEFIT_QUOTES: {
      // Landing Page
      GET_REFERENCE_DATA_LANDING_PAGE: `${BASE_BENEFIT_QUOTE_URL}/landing/reference-data`,
      SEARCH: `${BASE_BENEFIT_QUOTE_URL}/search`,
      // Multi-Step Form
      GET_REFERENCE_DATA_MULTI_STEP_FORM: `${BASE_BENEFIT_QUOTE_URL}/reference-data`,
      GET_MEMBERS_PRODUCTS_AND_MODALITIES: `${BASE_BENEFIT_QUOTE_URL}/member-products`,
      GET_SUBURBS_FOR_SEARCH_TERM: `${BASE_KLEBER_URL}/locality`,
      GET_PROVIDERS_FOR_SELECTED_MODALITY: `${BASE_BENEFIT_QUOTE_URL}/providers`,
      GET_SERVICES_FOR_SELECTED_MODALITY: `${BASE_BENEFIT_QUOTE_URL}/services`,
      GET_BENEFIT_QUOTE_DETAILS: (memberNumber, personCode, benefitQuoteId, productCode) => {
        return `${BASE_BENEFIT_QUOTE_URL}/membership/${memberNumber}/member/${personCode}/benefitquote/${benefitQuoteId}?productCode=${productCode}`;
      },
      SUBMIT_BENEFIT_QUOTE: (memberNumber, accessingPersonCode) => {
        return `${BASE_BENEFIT_QUOTE_URL}/membership/${memberNumber}/member/${accessingPersonCode}`;
      },
    },
    CLAIMS: {
      // Landing Page
      GET_REFERENCE_DATA_LANDING_PAGE: (memberNumber) => {
        return `${BASE_CLAIMS_URL}/membership/${memberNumber}/landing/reference-data`;
      },
      GET_CLAIMS_SUMMARY: (memberNumber) => {
        return `${BASE_CLAIMS_URL}/membership/${memberNumber}`;
      },
      // Multi-Step Form
      GET_REFERENCE_DATA_MULTI_STEP_FORM: `${BASE_CLAIMS_URL}/reference-data`,
      GET_CLAIMS_ACCOUNT_MEMBERS_PRODUCTS_AND_MODALITIES: `${BASE_CLAIMS_URL}`,
      SUBMIT_CLAIM: (memberNumber, personCode) => {
        return `${BASE_CLAIMS_URL}/membership/${memberNumber}/member/${personCode}`;
      },
      FILE_UPLOAD: (uploaderIdentifier, category, timestamp) => {
        return `${BASE_COMMON_URL}/file-upload/${uploaderIdentifier}/${category}?dateTimeStamp=${timestamp}`;
      },
      GET_CLAIM_DETAILS: (memberNumber, personCode, claimId, productCode) => {
        return `${BASE_CLAIMS_URL}/membership/${memberNumber}/member/${personCode}/claims/${claimId}?productCode=${productCode}`;
      },
      SUBMIT_SNAP_AND_SEND: (memberNumber) => {
        return `${BASE_CLAIMS_URL}/membership/${memberNumber}/snap-and-send`;
      },
    },
    LIMITS_USAGE: {
      GET_REFERENCE_DATA: `${BASE_LIMITS_USAGE_URL}/reference-data`,
      GET_LIMITS_AND_MODALITIES: (memberNumber, asAtDate) => {
        return `${BASE_LIMITS_USAGE_URL}/${memberNumber}/modalities-and-limits?asAtDate=${asAtDate}`;
      },
      GET_REPLACEMENT_PERIODS: `${BASE_LIMITS_USAGE_URL}/replacement-period`,
      GET_MEMBER_HISTORY: (memberNumber) => {
        return `${BASE_API_URL}/membership/${memberNumber}/history`;
      },
      GET_SERVICE_LIMITS: (memberNumber, asAtDate, currentDate) => {
        return `${BASE_LIMITS_USAGE_URL}/${memberNumber}/service-limits?asAtDate=${asAtDate}&currentDate=${currentDate}`;
      },
    },
  },
  MEMBERSHIP_CARD: {
    // Docs doesn't have this sending address data
    GET_LAST_ORDER: (memberNumber) => {
      return `${BASE_MEMBERSHIP_CARD_URL}/${memberNumber}/member-cards/last-card-order`;
    },
    GET_POSTAL_ADDRESS: (memberNumber, currentDate, mostRecentAddress) => {
      return `${BASE_MEMBERSHIP_CARD_URL}/${memberNumber}/member-cards/get-postal-address?mostRecentAddress=${mostRecentAddress}&currentDate=${currentDate}`;
    },
    GET_REFERENCE_DATA: `${BASE_MEMBERSHIP_CARD_URL}/member-cards/reference-data`,
    SUBMIT_NEW_CARD_ORDER: (memberNumber) => {
      return `${BASE_MEMBERSHIP_CARD_URL}/${memberNumber}/member-cards`;
    },
  },
  REBATE_CHANGE: {
    GET_REBATE_TIER: `${BASE_REBATE_CHANGE_URL}`,
    GET_REFERENCE_DATA: `${BASE_REBATE_CHANGE_URL}/reference-data`,
    SUBMIT_REBATE_TIER: `${BASE_REBATE_CHANGE_URL}/change`,
  },
  TAX_STATEMENTS: {
    GET_REFERENCE_DATA: `${BASE_TAX_STATEMENTS_URL}/reference-data`,
    GET_TAX_STATEMENTS_SUMMARY: (memberNumber) => {
      return `${BASE_TAX_STATEMENTS_URL}/membership/${memberNumber}`;
    },
    GET_TAX_STATEMENT_DOCUMENT: (memberNumber, accessingPersonCode, documentId) => {
      return `${BASE_TAX_STATEMENTS_URL}/membership/${memberNumber}/member/${accessingPersonCode}/documents/${documentId}`;
    },
    GENERATE_TAX_STATEMENT: (memberNumber) => {
      return `${BASE_TAX_STATEMENTS_URL}/membership/${memberNumber}/generate`;
    },
  },
  PAYMENTS: {
    GET_REFERENCE_DATA: (memberNumber) => {
      return `${BASE_PAYMENTS_URL}/${memberNumber}/reference-data`;
    },
    GET_PAYMENTS: (memberNumber) => {
      return `${BASE_PAYMENTS_URL}/membership/${memberNumber}/payment-history`;
    },
    MAKE_A_PAYMENT: {
      GET_REFERENCE_DATA: `${BASE_MAKE_A_PAYMENT_URL}/reference-data`,
      GET_PREMIUM_ACCOUNT: (memberNumber) => {
        return `${BASE_PAYMENTS_URL}/${memberNumber}/accounts/premium`;
      },
      GET_MAXIMUM_PAYMENT_AMOUNT: (memberNumber) => {
        return `${BASE_MAKE_A_PAYMENT_URL}/${memberNumber}/maximum-payment`;
      },
      MAKE_PAYMENT: (memberNumber) => {
        return `${BASE_MAKE_A_PAYMENT_URL}/${memberNumber}`;
      },
      GET_PAYMENT_SUCCESS_REFERENCE_DATA: `${BASE_MAKE_A_PAYMENT_URL}/payment-success/reference-data`,
    },
  },
  MEMBERSHIP_DETAILS: {
    GET_MEMBERSHIPS: `${BASE_MEMBERSHIP_DETAILS_URL}/membership-summary`,
  },
  MEMBER_DETAILS: {
    // Member Selection
    POST_GET_MEMBERS: `${BASE_MEMBER_DETAILS_URL}/member-selection`,
    GET_MEMBER_SELECTION_CONTENTS: `${BASE_MEMBER_DETAILS_URL}/member-selection/reference-data`,
    GET_MEMBER_DETAILS: (memberNumber, accessingPersonCode, currentDate) => {
      return `${BASE_MEMBER_DETAILS_URL}/membership/${memberNumber}/member/${accessingPersonCode}?currentDate=${currentDate}`;
    },
    // Edit Member Details
    PATCH_MEMBER_DETAILS: (memberNumber, accessingPersonCode) => {
      return `${BASE_MEMBER_DETAILS_URL}/submit-member-details/membership/${memberNumber}/member/${accessingPersonCode}`;
    },
    GET_MEMBER_DETAILS_REFERENCE_DATA: `${BASE_MEMBER_DETAILS_URL}/reference-data`,
    GET_NO_IMPACT_CONTENTS: `${BASE_MEMBER_DETAILS_URL}/no-impact/reference-data`,
    GET_PREMIUM_IMPACT_CONTENTS: `${BASE_MEMBER_DETAILS_URL}/premium-impact/reference-data`,
  },
  SUPPORT: {
    CONTACT_US: {
      GET_REFERENCE_DATA: `${BASE_API_URL}/contact-us/portal`,
      GET_CONFIRMATION_REFERENCE_DATA: `${BASE_API_URL}/contact-us/portal/confirmation`,
    },
  },
  MY_COVER: {
    PREMIUM_CALCULATOR: {
      GET_REFERENCE_DATA: (memberNumber) => {
        return `${BASE_API_URL}/premium-calculator/membership/${memberNumber}/reference-data`;
      },
      GET_RETRIEVE_PAYMENT_DATE: (memberNumber) => {
        return `${BASE_API_URL}/retrieve-payment-date/membership/${memberNumber}`;
      },
    },
    MY_DOCUMENTS: {
      GET_DOCUMENT_SUMMARY: (memberNumber) => {
        return `${BASE_MY_DOCUMENTS_URL}/membership/${memberNumber}`;
      },
      GET_DOCUMENT: (memberNumber, accessingPersonCode, documentId) => {
        return `${BASE_MY_DOCUMENTS_URL}/membership/${memberNumber}/member/${accessingPersonCode}/documents/${documentId}`;
      },
      GET_REFERENCE_DATA: `${BASE_MY_DOCUMENTS_URL}/reference-data`,
    },
    WAITING_PERIODS: {
      GET_REFERENCE_DATA: `${BASE_API_URL}/waiting-periods/reference-data`,
      GET_WAITING_PERIODS: (memberNumber, currentDate) => {
        return `${BASE_API_URL}/waiting-periods/${memberNumber}?currentDate=${currentDate}`;
      },
    },
    CHANGE_COVER: {
      RETRIEVE_COVER_DETAILS: (memberNumber, currentDate, productAndExcessDetailsOnly = false) => {
        return `${BASE_CHANGE_COVER_URL}/${memberNumber}/cover-details?currentDate=${currentDate}&productAndExcessDetailsOnly=${productAndExcessDetailsOnly}`;
      },
      GET_FUND_NAMES: `${BASE_COMMON_URL}/fund-names`,
      GET_TERMINATION_REASONS: (currentDate) => {
        return `${BASE_CHANGE_COVER_URL}/termination-reasons?currentDate=${currentDate}`;
      },
      GET_DUPLICATE_MEMBERS: `${BASE_CHANGE_COVER_URL}/duplicate-members`,
      GET_PERSON_OVER_COVERAGE: (personCode, currentDate) => {
        return `${BASE_CHANGE_COVER_URL}/duplicate-member-info?personCodes=${personCode}&currentDate=${currentDate}`;
      },
      GET_PRODUCT_RECOMMENDATION: `${BASE_CHANGE_COVER_URL}/get-product-recommendation`,
      SUBMIT_CHANGE_COVER: (memberNumber) => {
        return `${BASE_CHANGE_COVER_URL}/${memberNumber}/submit`;
      },
      LANDING: {
        GET_REFERENCE_DATA: `${BASE_CHANGE_COVER_URL}/change-my-cover/reference-data`,
      },
      CHANGE_PRODUCT: {
        GET_REFERENCE_DATA: `${BASE_CHANGE_COVER_URL}/change-my-product/reference-data`,
      },
      POST_EMAIL_QUOTE: (memberNumber) => {
        return `${BASE_CHANGE_COVER_URL}/${memberNumber}/email-quote`;
      },
      FETCH_MEMBER_HISTORY: (memberNumber, personCode, currentDate) => {
        return `${BASE_CHANGE_COVER_URL}/${memberNumber}/member/${personCode}/member-product-history?currentDate=${currentDate}`;
      },
      ADD_REMOVE_PERSON: {
        GET_REFERENCE_DATA: `${BASE_CHANGE_COVER_URL}/add-remove-person/reference-data`,
        RETRIEVE_NEW_SCALE: (memberNumber) => {
          return `${BASE_CHANGE_COVER_URL}/${memberNumber}/retrieve-new-scale`;
        },
        RETRIEVE_NEW_PREMIUM: (memberNumber) => {
          return `${BASE_CHANGE_COVER_URL}/${memberNumber}/estimate-new-premium`;
        },
        GET_PREMIUM_IMPACT_REF_DATA: `${BASE_CHANGE_COVER_URL}/premium-impact/reference-data`, // This ref data is for Premium Impact page for Add/Remove person flow
      },
      CHANGE_EXCESS: {
        GET_REFERENCE_DATA: `${BASE_CHANGE_EXCESS_URL}/change-my-excess/reference-data`,
        GET_PREMIUM_IMPACT_REFERENCE_DATA: `${BASE_CHANGE_EXCESS_URL}/premium-impact/reference-data`,
        GET_PREMIUM_ON_NEW_EXCESS: (memberNumber, excess, productAndExcessDetailsOnly = false) => {
          return `${BASE_CHANGE_COVER_URL}/${memberNumber}/excess/${excess}/premium?currentDate=${getCurrentDate()}&productAndExcessDetailsOnly=${productAndExcessDetailsOnly}`;
        },
        SUBMIT_NEW_EXCESS: (memberNumber) => {
          return `${BASE_CHANGE_COVER_URL}/${memberNumber}/submit-new-excess?currentDate=${getCurrentDate()}`;
        },
      },
    },
    REBATES: {
      GET_REBATE_HISTORY: (memberNumber, currentDate) => {
        return `${BASE_REBATES_URL}/${memberNumber}/history?currentDate=${currentDate}`;
      },
      GET_REBATE_LANDING_REF_DATA: `${BASE_REBATES_URL}/rebate-landing-page/reference-data`,
      GET_REBATE_HISTORY_REF_DATA: `${BASE_REBATES_URL}/rebate-tier-history/reference-data`,
      GET_ADD_REBATE_REF_DATA: `${BASE_REBATES_URL}/add-a-rebate/reference-data`,
      POST_ADD_REBATE: (memberNumber) => {
        return `${BASE_REBATES_URL}/${memberNumber}/add`;
      },
      GET_UPDATE_REBATE_REF_DATA: `${BASE_REBATES_URL}/update-your-rebate-tier/reference-data`,
      PATCH_UPDATE_REBATE: (memberNumber) => {
        return `${BASE_REBATES_URL}/${memberNumber}/update`;
      },
      GET_WITHDRAW_REBATE_REF_DATA: `${BASE_REBATES_URL}/withdraw-rebate/reference-data`,
      POST_ESTIMATE_NEW_REBATE_PREMIUM: (memberNumber) => {
        return `${BASE_REBATES_URL}/${memberNumber}/estimate-new-rebate-premium`;
      },
      POST_WITHDRAW_REBATE: (memberNumber) => {
        return `${BASE_REBATES_URL}/${memberNumber}/withdraw`;
      },
    },
  },
  PREMIUM_IMPACT: {
    GET_PREMIUM_ADJUSTMENT_PRICES_CRM: `${BASE_CRM_API_URL}/dhl_PremiumAdjustments`,
    GET_PREMIUM_ADJUSTMENT_PRICES: (memberNumber, showPremiumPaymentAmount, currentDate) => {
      return `${BASE_API_URL}/premium-adjustments/${memberNumber}?showPremiumPaymentAmount=${showPremiumPaymentAmount}&currentDate=${currentDate}`;
    },
  },
  PAYMENT_DETAILS: {
    GET_PAYMENT_DETAILS_SELECTION_REFERENCE_DATA: `${BASE_PAYMENT_DETAILS_REF_DATA_URL}/payment-details-selection`,
    GET_PAYMENT_DETAILS_REFERENCE_DATA: `${BASE_PAYMENT_DETAILS_REF_DATA_URL}/payment-details`,
    GET_PREMIUM_IMPACT_REF_DATA: `${BASE_PAYMENT_DETAILS_REF_DATA_URL}/payment-details-premium-impact`,
    GET_PAYMENT_DETAILS: (memberNumber, accessingPersonCode, currentDate) => {
      return `${BASE_API_URL}/account-details/membership/${memberNumber}/member/${accessingPersonCode}?currentDate=${currentDate}`;
    },
    PATCH_PAYMENT_DETAILS: (memberNumber, accessingPersonCode) => {
      return `${BASE_API_URL}/account-details/membership/${memberNumber}/member/${accessingPersonCode}/submit`;
    },
  },
};

export default Services;
